import type { Location as RouterLocation } from 'react-router';

import { parseReturnPath } from '@/lib/routes';

type RedirectLocation = Pick<RouterLocation, 'pathname' | 'search' | 'hash'>;
type RedirectSearchParams = Pick<URLSearchParams, 'get'>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isRedirectLocation(value: unknown): value is RedirectLocation {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value['pathname'] === 'string' &&
    typeof value['search'] === 'string' &&
    typeof value['hash'] === 'string'
  );
}

export function buildRedirectPath(location: RedirectLocation): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

function parseRedirectTarget(value: string, fallback: string): string {
  return parseReturnPath(value.trim()) ?? fallback;
}

export function readRedirectPathFromSearchParams(
  searchParams: RedirectSearchParams,
  fallback = '/',
): string {
  const redirectTarget = searchParams.get('redirect');
  return redirectTarget === null ? fallback : parseRedirectTarget(redirectTarget, fallback);
}

export function readRedirectPath(state: unknown, fallback = '/'): string {
  if (!isRecord(state) || !('from' in state)) {
    return fallback;
  }

  const redirectTarget = state['from'];
  if (typeof redirectTarget === 'string') {
    return parseRedirectTarget(redirectTarget, fallback);
  }

  if (isRedirectLocation(redirectTarget)) {
    return parseRedirectTarget(buildRedirectPath(redirectTarget), fallback);
  }

  return fallback;
}
